import { useSearchParams } from 'react-router-dom';
import { Field, FormikProvider, useFormik } from 'formik';
import {
  formValuesFromUrlSearchParams,
  mergeFormValuesVsCurrentSearchParams, SerializableValues
} from 'utils/url-search-params.helper';
import { ReactElement, useEffect, useMemo } from 'react';
import { PAGE_FIELD_NAME } from 'shared/hooks/paging-params';

export type BaseUrlParamsFilterProps = {
  queryParamName: string,
  size?: 'small' | 'medium',
  label?: string,
  withNonSelect?: boolean,
}

type Props<ValueType> = BaseUrlParamsFilterProps & {
  children: ReactElement<typeof Field>;
  defaultValue?: ValueType | null;
}

/**
 * Когда используете данный фильтр он просто обновляет URLSearchParams а адресной строке браузера.
 * Компонент списка должен подписаться на URLSearchParams и обновлять состояние соответственно
 */
export const BaseUrlParamsFilter = <ValueType extends SerializableValues, >({ queryParamName, defaultValue = null as ValueType | null, children }: Props<ValueType>) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const defaultValues = useMemo<Record<string, ValueType | null>>(() => ({
    [queryParamName]: defaultValue
  }), [defaultValue, queryParamName]);

  const initialValues = useMemo(() => formValuesFromUrlSearchParams(searchParams, defaultValues), [defaultValues, searchParams]);

  const formik = useFormik<Record<string, ValueType | null>>({
    initialValues,
    enableReinitialize: true,
    onSubmit: () => {},
    validateOnChange: true
  });

  const formValue: ValueType | null = formik.values[queryParamName] ?? null;
  const formValues = useMemo<Record<string, ValueType | null>>(() => ({ [queryParamName]: formValue }), [formValue, queryParamName]);

  useEffect(() => {
    if (!formik.isValid || !formik.dirty) return;
    if (JSON.stringify(formik.initialValues) !== JSON.stringify(initialValues)) return;

    const params = mergeFormValuesVsCurrentSearchParams(searchParams, formValues, defaultValues);
    params.delete(PAGE_FIELD_NAME);
    if (params.toString() === searchParams.toString()) return;

    setSearchParams(params, { replace: true });
  }, [defaultValues, formValues, formik.isValid, formik.dirty, formik.initialValues, initialValues, searchParams, setSearchParams]);

  return (
    <FormikProvider value={formik}>
      {children}
    </FormikProvider>
  );
};
